/**
 * The device's cookie-consent answers: the `_cf_consent_<type>` browser cookies
 * (`'1'` granted, `'0'` denied) that telemetry obeys, and the per-type toggles
 * the cookie banner edits before they are saved.
 *
 * This is only the storage. Deciding WHEN the cookie banner shows, and the
 * account Terms-of-Service gate, stay in `components/consent-dialog/`. The two
 * used to share one component; see design-docs/boot-sequence.md, "Consent
 * re-evaluation after the CouchDB login (#4219)", for how account consent is
 * copied onto these cookies.
 */
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import * as Cookie from 'es-cookie';
import { UserConsentId, UserConsentRecords } from '@cf-shared/user-consent-interface';
import { AdConsentAnswer } from '@cf-shared/ad-consent';
import { resolveEffectiveAdConsent, writeAdConsentCookies, writeConsentCookie } from 'services/ad-consent-cookie';
import { TelemetryService } from 'services/telemetry.service';
import { LL } from 'i18n/i18n.service';
import { COOKIE_CATEGORIES, CookieCategoryId, CookieChoice, cookieValueToChoice, everyCookieChoiceMade } from 'services/cookie-consent-rules';

// keep this in sync with index.html
/**
 * The per-type answers, as edited by the cookie banner before they are saved.
 *
 * `null` means NOT ANSWERED — the toggle draws itself in its neutral position,
 * neither on nor off. A first-time visitor's optional categories all start there
 * (#5712), so nothing is chosen on their behalf; the Save button stays disabled
 * until each one has been set. A returning visitor's cookies give real booleans,
 * so they always see what they actually chose.
 */
export type ConsentTypeHolder = Partial<Record<CookieCategoryId, boolean | null>>;

/** Whether every optional cookie category has an answer, so the customize view's
 *  Save Settings may be pressed (#5712): the shared rule with this app's category
 *  list filled in. The cast is where reading by arbitrary id is allowed, so the
 *  holder itself keeps its named keys. */
export function everyCookieCategoryAnswered(values: ConsentTypeHolder): boolean {
  return everyCookieChoiceMade(values as { [id: string]: CookieChoice }, COOKIE_CATEGORIES);
}

export interface ConsentTypeDescription {
  id: string;
  getName: () => string;
  getDescription: () => string;
  required?: boolean;
}

/** The translated name and description for each category id. Split from the list
 *  itself so the ids and their order stay in `cookie-consent-rules.ts`, where the
 *  rules and their tests can read them without Angular. */
const COOKIE_CATEGORY_TEXTS: Record<CookieCategoryId, { getName: () => string, getDescription: () => string }> = {
  functionality: {
    getName: () => LL.consent.cookies.types.functionality.name(),
    getDescription: () => LL.consent.cookies.types.functionality.description(),
  },
  security: {
    getName: () => LL.consent.cookies.types.security.name(),
    getDescription: () => LL.consent.cookies.types.security.description(),
  },
  personalization: {
    getName: () => LL.consent.cookies.types.personalization.name(),
    getDescription: () => LL.consent.cookies.types.personalization.description(),
  },
  ad: {
    getName: () => LL.consent.cookies.types.advertising.name(),
    getDescription: () => LL.consent.cookies.types.advertising.description(),
  },
  analytics: {
    getName: () => LL.consent.cookies.types.performance.name(),
    getDescription: () => LL.consent.cookies.types.performance.description(),
  },
};

export const allCookieConsentTypes: ConsentTypeDescription[] =
  COOKIE_CATEGORIES.map(c => ({ id: c.id, required: c.required, ...COOKIE_CATEGORY_TEXTS[c.id] }));

// the categories accepting the Terms switches on for a device that has not
// answered them. Advertising is deliberately not one of them (#5587).
const ACCEPTED_WITH_TERMS_COOKIE_IDS: CookieCategoryId[] =
  ['functionality', 'security', 'analytics', 'personalization'];

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  // the per-type answers as last read or edited; the cookie banner's toggles
  // bind straight into this object, so it is edited in place and never replaced
  public readonly values: ConsentTypeHolder = {};

  constructor(private telemetry: TelemetryService) {}

  // load every type's answer from its cookie. A MISSING cookie is not an answer,
  // so it reads as null and the toggle shows neutral (#5712) — a cookie the person
  // has never been asked about must not be drawn as a decision they made.
  readFromCookies() {
    for (let c of allCookieConsentTypes) {
      this.values[c.id] = cookieValueToChoice(Cookie.get('_cf_consent_' + c.id));
    }
    // Advertising has its own reading rules (#5587): an old undated '1' proves
    // nothing, because signing in used to write it for every account. The Global
    // Privacy Control signal is applied here too (#5663), so a browser sending it
    // sees the toggle OFF rather than neutral — that is the state the app is
    // actually acting on, and the dialog says why beside it (#5682).
    const effective = resolveEffectiveAdConsent();
    this.values.ad = effective === undefined ? null : effective === AdConsentAnswer.Yes;
  }

  /**
   * Save every type's answer to its cookie, then tell telemetry.
   *
   * Only a real answer reaches here — the cookie banner's own buttons — so the
   * Advertising answer is stamped with the time it was given (#5587). It is
   * returned so the caller can do what only it can: tell the rest of the app the
   * answer changed, and save it on the account of a signed-in user.
   */
  writeToCookies(): AdConsentAnswer {
    for (let c of allCookieConsentTypes) {
      if (c.id === 'ad') continue;
      writeConsentCookie(c.id, this.values[c.id] ? '1' : '0');
    }
    const adAnswer = this.values.ad ? AdConsentAnswer.Yes : AdConsentAnswer.No;
    writeAdConsentCookies(adAnswer, DateTime.utc().toISO());
    this.applyValues();
    return adAnswer;
  }

  // tell telemetry the device's cookie answers are known; it reads only the analytics answer
  applyValues() {
    // an unanswered analytics category is not a yes
    this.telemetry.cookiesProcessed(!!this.values.analytics);
  }

  // grant every type in memory; nothing is saved until writeToCookies
  enableAll() {
    for (let c of allCookieConsentTypes) {
      this.values[c.id] = true;
    }
  }

  /**
   * Copy what accepting the Terms grants onto the device cookies.
   *
   * Only for a category this device has NO answer for (#5587). Overwriting an
   * answer here is what used to undo Reject All at every sign-in.
   *
   * Advertising is deliberately absent: it is never inferred from the Terms, and
   * never from the Marketing record, which is about emails — the code this
   * replaced switched it on whenever a Marketing record existed, without even
   * reading whether that record said yes. The account's own Advertising answer is
   * lined up with the device's by the consent dialog, which owns the account side.
   */
  applyAccountConsents(consents: UserConsentRecords | undefined) {
    // if the account has no ToS record (or the CouchDB login has not delivered it yet), do nothing
    if (!consents || !consents[UserConsentId.ToS]) return;
    for (const id of ACCEPTED_WITH_TERMS_COOKIE_IDS) {
      if (Cookie.get('_cf_consent_' + id) === undefined) {
        writeConsentCookie(id, '1');
        this.values[id] = true;
      }
    }
    // Functionality is required, whatever an old cookie says
    if (!this.values.functionality) {
      writeConsentCookie('functionality', '1');
      this.values.functionality = true;
    }
    this.applyValues();
  }
}
